import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { can, type Permission } from "../lib/roles";
import { PageHeader } from "../components/Surface";
import { Icon } from "../app/Icon";
import { adminQueue } from "../lib/verification";
import { listReports } from "../lib/moderation";
import { adminListTickets } from "../lib/support";
import { adminListFeedback } from "../lib/feedback";
import { adminMetrics, type PlatformMetrics } from "../lib/admin";
import { securityDashboard } from "../lib/security";

interface Card {
  perm: Permission;
  to: string;
  icon: string;
  label: string;
  desc: string;
  count?: number;
}

export function AdminHome() {
  const { roles } = useAuth();
  const isStaff = ["super_admin", "admin", "moderator", "support", "staff"].some((r) => roles.includes(r));
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);

  useEffect(() => {
    if (!isStaff) return;
    const jobs: Promise<void>[] = [];
    jobs.push(adminMetrics().then((r) => setMetrics(r.metrics)).catch(() => undefined));
    if (can(roles, "admin_verification")) jobs.push(adminQueue("pending").then((r) => setCount("verify", r.queue.length)).catch(() => undefined));
    if (can(roles, "admin_moderation")) jobs.push(listReports("reported").then((r) => setCount("reports", r.counts?.reported ?? 0)).catch(() => undefined));
    if (can(roles, "admin_support")) jobs.push(adminListTickets().then((r) => setCount("support", (r.counts?.open ?? 0) + (r.counts?.in_progress ?? 0))).catch(() => undefined));
    if (can(roles, "admin_feedback")) jobs.push(adminListFeedback().then((r) => setCount("feedback", r.counts?.new ?? 0)).catch(() => undefined));
    if (can(roles, "admin_security")) jobs.push(securityDashboard().then((r) => setCount("security", r.suspended_count ?? 0)).catch(() => undefined));
    void Promise.all(jobs);
    function setCount(k: string, n: number) { setCounts((c) => ({ ...c, [k]: n })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  if (!isStaff) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Admin" subtitle="Staff access required." />
        <p className="font-mono text-sm text-danger">Staff only.</p>
      </div>
    );
  }

  const cards = ([
    { perm: "admin_verification", to: "/admin/verification", icon: "admin", label: "Review queue", desc: "Approve identity + credential submissions.", count: counts.verify },
    { perm: "admin_users", to: "/admin/users", icon: "connections", label: "Users & roles", desc: "Search accounts, manage roles, suspend." },
    { perm: "admin_moderation", to: "/admin/moderation", icon: "flag", label: "Moderation", desc: "Triage reported content.", count: counts.reports },
    { perm: "admin_support", to: "/admin/support", icon: "help", label: "Support", desc: "Respond to support tickets.", count: counts.support },
    { perm: "admin_feedback", to: "/admin/feedback", icon: "idea", label: "Feedback", desc: "Triage product feedback.", count: counts.feedback },
    { perm: "admin_security", to: "/admin/security", icon: "security", label: "Attack dashboard", desc: "Spot abusive accounts; suspend or terminate.", count: counts.security },
    { perm: "admin_users", to: "/admin/audit", icon: "security", label: "Audit log", desc: "Trace sensitive staff actions." },
    { perm: "admin_users", to: "/admin/flags", icon: "settings", label: "Feature flags", desc: "Toggle platform features (audited)." },
  ] as Card[]).filter((c) => can(roles, c.perm));

  const kpis: { label: string; value: number }[] = metrics ? [
    { label: "Accounts", value: metrics.accounts },
    { label: "Verified", value: metrics.verified_accounts },
    { label: "Open jobs", value: metrics.open_jobs },
    { label: "Open loads", value: metrics.open_loads },
    { label: "Open disputes", value: metrics.open_disputes },
    { label: "Open reports", value: metrics.open_reports },
    { label: "Open tickets", value: metrics.open_tickets },
    { label: "New (7d)", value: metrics.new_accounts_7d },
  ] : [];

  return (
    <div className="grid gap-6">
      <PageHeader title="Admin console" subtitle="Run the marketplace — everything staff needs in one place." />

      {metrics && (
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="platform-metrics">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-card border border-line bg-surface p-4 text-center">
              <dt className="font-mono text-[11px] uppercase tracking-widest text-muted">{k.label}</dt>
              <dd className="font-display text-2xl font-black tracking-tight">{k.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <Link key={c.to + c.label} to={c.to} className="grid content-start gap-2 rounded-card border border-line bg-surface p-5 transition-colors hover:border-accent">
            <div className="flex items-center justify-between">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-accent/15 text-accent-strong"><Icon name={c.icon} className="h-5 w-5" /></span>
              {c.count != null && c.count > 0 && (
                <span className="rounded-full bg-accent px-2 py-0.5 font-mono text-xs font-bold text-accent-ink">{c.count}</span>
              )}
            </div>
            <span className="font-display text-base font-black tracking-tight">{c.label}</span>
            <span className="text-sm text-muted">{c.desc}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
